/**
 * RevenueRegisterController — page index annuel `/livre-des-recettes.html`
 * (EPIC-099 / 99.27 §7.H.C.1).
 *
 * Affiche 12 lignes mensuelles agrégées pour l'exercice sélectionné, avec
 * sous-totaux par source (paiements factures, détachés, sessions caisse),
 * badge statut (`not_confirmed`/`confirmed`/`sealed`/`future`), action « Voir »
 * pointant vers la page mois détail `/livre-des-recettes-detail.html?year=&month=`.
 *
 * Charge `GET /revenue/register?year=N`. Conditionnel HT/TVA : les
 * colonnes HT/TVA et le pied associé sont masqués si `meta.isVatSubject ===
 * false` (cas par défaut en franchise §11).
 *
 * Encart de tête « Clôturer l'exercice » : visible si `year < currentYear AND
 * !meta.closed`. Variante A (CTA solid) si tous les mois confirmés, variante B
 * (CTA ghost + avertissement) sinon.
 *
 * Convention `frontend/CLAUDE.md` : aucune construction HTML inline en JS,
 * uniquement clone de `<template>` + remplissage `data-slot` + manipulation
 * DOM élément par élément.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const MONTH_LABELS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const STATUS_LABELS = {
  not_confirmed: "Non confirmé",
  confirmed: "Confirmé",
  sealed: "Scellé",
  future: "—",
};

const STATUS_PILL_MODIFIER = {
  not_confirmed: "nm-pill--ochre",
  confirmed: "nm-pill--olive",
  sealed: null,
  future: null,
};

export default class RevenueRegisterController extends BaseController {
  static targets = [
    "yearSelect",
    "exportButton",
    "tbody",
    "tfoot",
    "thHt",
    "thVat",
    "totalEntries",
    "totalExclVat",
    "totalVat",
    "totalInclVat",
    "summary",
    "yearCloseCard",
    "yearCloseTitle",
    "yearCloseBody",
    "yearCloseButton",
    "retroactiveBanner",
    "retroactiveTitle",
    "hashDivergenceBanner",
    "hashDivergenceBody",
    "closuresTbody",
  ];

  connect() {
    this._fetchSeq = 0;
    this._populateYearSelect();
    this._restoreFromQueryString();
    this.refresh();
  }

  refresh() {
    this._syncQueryString();
    this._fetch();
  }

  _populateYearSelect() {
    if (!this.hasYearSelectTarget || this.yearSelectTarget.options.length > 0) return;
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 5; y--) {
      const opt = document.createElement("option");
      opt.value = String(y);
      opt.textContent = String(y);
      if (y === currentYear) opt.selected = true;
      this.yearSelectTarget.appendChild(opt);
    }
  }

  _restoreFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    if (params.has("year") && this.hasYearSelectTarget) {
      this.yearSelectTarget.value = params.get("year");
    }
  }

  _syncQueryString() {
    const year = this._currentYear();
    const params = new URLSearchParams();
    if (year) params.set("year", String(year));
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? "?" + qs : ""),
    );
  }

  _currentYear() {
    if (this.hasYearSelectTarget && this.yearSelectTarget.value) {
      return parseInt(this.yearSelectTarget.value, 10) || new Date().getFullYear();
    }
    return new Date().getFullYear();
  }

  async _fetch() {
    const seq = ++this._fetchSeq;
    const year = this._currentYear();
    try {
      const response = await api.listRevenueRegister({ year });
      if (seq !== this._fetchSeq) return;
      const meta = response?.meta ?? {};
      this._applyVatVisibility(Boolean(meta.isVatSubject));
      this._updateProvisionalExportVisibility(meta);
      this._renderMonthsTable(Array.isArray(response?.months) ? response.months : [], year);
      this._renderTotals(response?.totals ?? {});
      this._renderYearCloseCard(year, meta);
      this._renderClosures(Array.isArray(response?.closures) ? response.closures : []);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger le livre des recettes.";
      this.showError(msg);
      this._renderMonthsTable([], year);
    }
  }

  _applyVatVisibility(isVatSubject) {
    if (this.hasThHtTarget) this.thHtTarget.hidden = !isVatSubject;
    if (this.hasThVatTarget) this.thVatTarget.hidden = !isVatSubject;
    if (this.hasTotalExclVatTarget) this.totalExclVatTarget.hidden = !isVatSubject;
    if (this.hasTotalVatTarget) this.totalVatTarget.hidden = !isVatSubject;
    this._isVatSubject = isVatSubject;
  }

  _renderMonthsTable(months, year) {
    if (!this.hasTbodyTarget) return;
    this._clearChildren(this.tbodyTarget);

    for (const row of months) {
      const isFuture = row.status === "future";
      const templateId = isFuture
        ? "tpl-revenue-register-month-row-future"
        : "tpl-revenue-register-month-row";
      const fragment = this.cloneTemplate(templateId);
      const monthLabel = this._capitalize(MONTH_LABELS[row.month - 1] ?? "—");
      const href = `/livre-des-recettes-detail.html?year=${encodeURIComponent(year)}&month=${String(row.month).padStart(2, "0")}`;

      if (isFuture) {
        this.fillSlots(fragment, { monthLabel });
        const cellHt = fragment.querySelector('[data-slot-element="cellHtFuture"]');
        const cellVat = fragment.querySelector('[data-slot-element="cellVatFuture"]');
        if (cellHt) cellHt.hidden = !this._isVatSubject;
        if (cellVat) cellVat.hidden = !this._isVatSubject;
        this.tbodyTarget.appendChild(fragment);
        continue;
      }

      this.fillSlots(fragment, {
        monthLabel,
        entriesCount: String(row.entriesCount ?? 0),
        totalExclVat: this._formatAmount(row.totalExclVat) + " €",
        totalVat: row.totalVat === "0.00" ? "—" : this._formatAmount(row.totalVat) + " €",
        totalInclVat: this._formatAmount(row.totalInclVat) + " €",
        totalInvoices: row.totalInvoices === "0.00" ? "—" : this._formatAmount(row.totalInvoices) + " €",
        totalDetached: row.totalDetached === "0.00" ? "—" : this._formatAmount(row.totalDetached) + " €",
        totalCashSessions: row.totalCashSessions === "0.00" ? "—" : this._formatAmount(row.totalCashSessions) + " €",
        statusLabel: STATUS_LABELS[row.status] ?? row.status ?? "—",
      });

      const link = fragment.querySelector('[data-slot-element="actionLink"]');
      if (link) link.setAttribute("href", href);
      const monthLink = fragment.querySelector('[data-slot="monthLabel"]');
      if (monthLink && monthLink.tagName === "A") monthLink.setAttribute("href", href);

      const cellHt = fragment.querySelector('[data-slot-element="cellHt"]');
      const cellVat = fragment.querySelector('[data-slot-element="cellVat"]');
      if (cellHt) cellHt.hidden = !this._isVatSubject;
      if (cellVat) cellVat.hidden = !this._isVatSubject;

      const badge = fragment.querySelector('[data-slot-element="statusBadge"]');
      if (badge) {
        const modifier = STATUS_PILL_MODIFIER[row.status];
        if (modifier) badge.classList.add(modifier);
      }

      this.tbodyTarget.appendChild(fragment);
    }
  }

  _renderTotals(totals) {
    if (this.hasTfootTarget) this.tfootTarget.hidden = false;
    if (this.hasTotalEntriesTarget) {
      this.totalEntriesTarget.textContent = String(totals?.entriesCount ?? 0);
    }
    if (this.hasTotalExclVatTarget) {
      this.totalExclVatTarget.textContent = this._formatAmount(totals?.totalExclVat) + " €";
    }
    if (this.hasTotalVatTarget) {
      this.totalVatTarget.textContent = totals?.totalVat === "0.00"
        ? "—"
        : this._formatAmount(totals?.totalVat) + " €";
    }
    if (this.hasTotalInclVatTarget) {
      this.totalInclVatTarget.textContent = this._formatAmount(totals?.totalInclVat) + " €";
    }
  }

  _renderYearCloseCard(year, meta) {
    if (!this.hasYearCloseCardTarget) return;
    const currentYear = new Date().getFullYear();
    const isPastYear = year < currentYear;
    const isClosed = Boolean(meta?.closed);

    if (!isPastYear || isClosed) {
      this.yearCloseCardTarget.hidden = true;
      return;
    }

    this.yearCloseCardTarget.hidden = false;
    const confirmedCount = Number(meta?.confirmedMonthsCount ?? 0);
    const pendingCount = Math.max(0, 12 - confirmedCount);
    const allConfirmed = pendingCount === 0;

    if (this.hasYearCloseTitleTarget) {
      this.yearCloseTitleTarget.textContent = `Clôturer l'exercice ${year}`;
    }
    if (this.hasYearCloseBodyTarget) {
      this.yearCloseBodyTarget.textContent = allConfirmed
        ? `Tous les mois sont confirmés — vous pouvez clôturer définitivement l'exercice ${year} et archiver son PDF dans votre coffre-fort.`
        : `${pendingCount} mois non confirmé${pendingCount > 1 ? "s" : ""} sur l'exercice ${year}. La clôture reste possible : les mois manquants seront listés tels quels dans le PDF annuel.`;
    }
    if (this.hasYearCloseButtonTarget) {
      const btn = this.yearCloseButtonTarget;
      btn.textContent = `Clôturer l'exercice ${year}`;
      btn.dataset.closeYear = String(year);
      btn.classList.remove("nm-btn--solid", "nm-btn--ghost");
      btn.classList.add(allConfirmed ? "nm-btn--solid" : "nm-btn--ghost");
    }
  }

  _renderClosures(closures) {
    if (!this.hasClosuresTbodyTarget) return;
    this._clearChildren(this.closuresTbodyTarget);
    if (closures.length === 0) {
      const empty = this.cloneTemplate("tpl-revenue-register-closures-empty");
      if (empty) this.closuresTbodyTarget.appendChild(empty);
      return;
    }
    for (const closure of closures) {
      const fragment = this.cloneTemplate("tpl-revenue-register-closure-row");
      this.fillSlots(fragment, {
        fiscalYear: String(closure.fiscalYear ?? ""),
        closedAtLabel: this._formatDateTime(closure.closedAt),
        entriesCount: String(closure.entriesCount ?? "—"),
        entriesSealedHashShort: closure.entriesSealedHashShort ?? "—",
      });
      const timeEl = fragment.querySelector('[data-slot-attr-datetime="closedAtIso"]');
      if (timeEl && closure.closedAt) timeEl.setAttribute("datetime", closure.closedAt);
      const downloadLink = fragment.querySelector('[data-slot-element="closureDownloadLink"]');
      if (downloadLink) {
        downloadLink.setAttribute(
          "href",
          api.buildRevenueRegisterClosurePdfUrl({ year: closure.fiscalYear }),
        );
      }
      this.closuresTbodyTarget.appendChild(fragment);
    }
  }

  // Le bouton d'export provisoire (pied de tableau) est masqué pour un exercice
  // clôturé : le document de référence est alors le PDF officiel, téléchargeable
  // par ligne dans la table des clôtures.
  _updateProvisionalExportVisibility(meta) {
    if (!this.hasExportButtonTarget) return;
    this.exportButtonTarget.hidden = Boolean(meta?.closed);
  }

  exportProvisional(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    const year = this._currentYear();
    window.location.href = api.buildRevenueRegisterProvisionalExportUrl({ year });
  }

  _clearChildren(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  _capitalize(s) {
    if (!s || typeof s !== "string") return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  _formatAmount(raw) {
    if (raw === null || raw === undefined || raw === "") return "0,00";
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    return num.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  _formatDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
